<template>
    <teleport to="body">
        <div v-if="modelValue">
            <div class="modal-backdrop animated-backdrop" @click="handleBackdropClick"></div>
            <div class="modal-dialog-centered modal-animated">
                <div class="modal-content" :class="[modalClass, `modal-${size}`]">
                    <div class="modal-header">
                        <h3>
                            <i :class="icon"></i>
                            {{ title }}
                        </h3>
                        <button class="btn-close" @click="close">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <div class="modal-body" :class="{ 'no-padding': noPadding }" :style="{ maxHeight: bodyMaxHeight }">
                        <slot></slot>
                    </div>

                    <div v-if="showFooter" class="modal-footer">
                        <slot name="footer">
                            <button class="btn-secondary" @click="close">
                                Cancelar
                            </button>
                            <button class="btn-primary" :disabled="loading" @click="$emit('confirm')">
                                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                                <i v-else :class="confirmIcon"></i>
                                {{ confirmText }}
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup>
import '@/assets/css/modalstore.css';

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    title: {
        type: String,
        required: true
    },
    icon: {
        type: String,
        default: 'fas fa-edit'
    },
    confirmText: {
        type: String,
        default: 'Guardar'
    },
    confirmIcon: {
        type: String,
        default: 'fas fa-save'
    },
    loading: {
        type: Boolean,
        default: false
    },
    modalClass: {
        type: String,
        default: ''
    },
    closeOnBackdrop: {
        type: Boolean,
        default: true
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value)
    },
    noPadding: {
        type: Boolean,
        default: false
    },
    showFooter: {
        type: Boolean,
        default: true
    },
    bodyMaxHeight: {
        type: String,
        default: '70vh'
    }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'close']);

const close = () => {
    emit('update:modelValue', false);
    emit('close');
};

const handleBackdropClick = () => {
    if (props.closeOnBackdrop) {
        close();
    }
};
</script>

<style scoped>
/* ========================================
   MODAL MODERNO
======================================== */
.modal-content {
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

/* Tamaños del modal */
.modal-small {
    max-width: 400px;
}

.modal-medium {
    max-width: 600px;
}

.modal-large {
    max-width: 900px;
}

.modal-xlarge {
    max-width: 1200px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2rem;
    border-bottom: 2px solid #f1f5f9;
    background: linear-gradient(135deg, #fef2f2 0%, #fff 100%);
}

.modal-header h3 {
    margin: 0;
    font-size: 1.375rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.modal-header h3 i {
    font-size: 1.5rem;
    color: #ef4444;
}

.btn-close {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 1.25rem;
}

.btn-close:hover {
    background: #ef4444;
    border-color: #ef4444;
    color: white;
    transform: rotate(90deg);
}

.modal-body {
    padding: 2rem;
    overflow-y: auto;
    flex: 1;
}

.modal-body.no-padding {
    padding: 0;
}

.modal-footer {
    padding: 1.5rem 2rem;
    border-top: 2px solid #f1f5f9;
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    background: #fafbfc;
}

.btn-secondary,
.btn-primary,
.btn-danger {
    padding: 0.875rem 1.75rem;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
}

.btn-secondary {
    background: white;
    color: #334155;
    border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
    background: #667eea;
    border-color: #667eea;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.btn-primary {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

.btn-primary:active {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

.btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Variante para modal de confirmación */
.modal-content.modal-confirm {
    max-width: 500px;
}

/* ========================================
   RESPONSIVE
======================================== */
@media (max-width: 768px) {
    .modal-content {
        max-width: 100%;
        border-radius: 16px;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 1.5rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn-secondary,
    .btn-primary,
    .btn-danger {
        width: 100%;
        justify-content: center;
    }
}
</style>
